import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface OnlineUser {
  user_id: string;
  first_name: string;
  last_name: string;
  profile_photo_url: string | null;
}

export const usePresence = (companyId: string | null) => {
  const { user } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);

  useEffect(() => {
    if (!companyId || !user?.id) return;

    const channel = supabase.channel(`presence-${companyId}`, {
      config: { presence: { key: user.id } },
    });

    channel
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState();
        const users: OnlineUser[] = [];
        for (const key of Object.keys(state)) {
          const presences = state[key] as any[];
          if (presences?.[0]) {
            users.push({
              user_id: key,
              first_name: presences[0].first_name ?? "",
              last_name: presences[0].last_name ?? "",
              profile_photo_url: presences[0].profile_photo_url ?? null,
            });
          }
        }
        setOnlineUsers(users);
      })
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          // Fetch current user's profile info to broadcast
          const { data: profile } = await (supabase.from("user_profiles") as any)
            .select("first_name, last_name, profile_photo_url")
            .eq("user_id", user.id)
            .single();
          await channel.track({
            first_name: profile?.first_name ?? "",
            last_name: profile?.last_name ?? "",
            profile_photo_url: profile?.profile_photo_url ?? null,
            online_at: new Date().toISOString(),
          });
        }
      });

    return () => { supabase.removeChannel(channel); };
  }, [companyId, user?.id]);

  const isOnline = (userId: string) => onlineUsers.some(u => u.user_id === userId);

  return { onlineUsers, isOnline };
};
